import { z } from 'zod';
import { WarningDaysSchema } from '@suite/contracts-kernel';
import { DOCUMENT_COMPLIANCE_STATUSES } from './configurable-engine.js';

/**
 * fleet-compliance.ts
 * fleet-qr-compliance PR6 (design.md §8.2) — aggregated per-asset compliance
 * row for the fleet-wide grid (`GET /servicios/assets/fleet-compliance`,
 * `FleetComplianceView.vue`). Combines the worst vehicle_document status
 * with the checklist submission status into ONE traffic-light level — see
 * domain/fleet-compliance.ts for the actual computation.
 */

export const CHECKLIST_COMPLIANCE_STATUSES = ['alDia', 'atrasado', 'sinChecklist'] as const;
export type ChecklistComplianceStatus = (typeof CHECKLIST_COMPLIANCE_STATUSES)[number];

export const FLEET_COMPLIANCE_LEVELS = ['verde', 'amarillo', 'rojo'] as const;
export type FleetComplianceLevel = (typeof FLEET_COMPLIANCE_LEVELS)[number];

export const FleetComplianceRowSchema = z.object({
  assetId: z.string().uuid(),
  serialNumber: z.string(),
  description: z.string().nullable(),
  status: z.string(),
  documentStatus: z.enum(DOCUMENT_COMPLIANCE_STATUSES),
  checklistStatus: z.enum(CHECKLIST_COMPLIANCE_STATUSES),
  lastChecklistSubmittedAt: z.string().datetime().or(z.date()).nullable(),
  complianceLevel: z.enum(FLEET_COMPLIANCE_LEVELS),
});
export type FleetComplianceRow = z.infer<typeof FleetComplianceRowSchema>;

export const FleetComplianceListResponseSchema = z.object({
  items: z.array(FleetComplianceRowSchema),
  totalCount: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});
export type FleetComplianceListResponse = z.infer<typeof FleetComplianceListResponseSchema>;

// ---------------------------------------------------------------------------
// Vehicle document type editor — /servicios/admin/vehicle-document-types.
// presentation_type is a closed vocabulary (CHECK in the migration); null means
// "not set". warningDays: days ahead a document of the type turns "por vencer".
// ---------------------------------------------------------------------------

export const VEHICLE_DOCUMENT_PRESENTATION_TYPES = ['na', 'anual', 'bianual', 'semestral'] as const;
export type VehicleDocumentPresentationType = (typeof VEHICLE_DOCUMENT_PRESENTATION_TYPES)[number];

const VehicleDocumentTypeNameSchema = z.string().trim().min(1).max(120);
const PresentationTypeSchema = z.enum(VEHICLE_DOCUMENT_PRESENTATION_TYPES).nullable();

export const VehicleDocumentTypeAdminCreateSchema = z
  .object({
    name: VehicleDocumentTypeNameSchema,
    requiresExpiration: z.boolean().default(true),
    presentationType: PresentationTypeSchema.optional(),
    warningDays: WarningDaysSchema.optional(),
  })
  .strict();
export type VehicleDocumentTypeAdminCreate = z.infer<typeof VehicleDocumentTypeAdminCreateSchema>;

export const VehicleDocumentTypeAdminUpdateSchema = z
  .object({
    name: VehicleDocumentTypeNameSchema.optional(),
    requiresExpiration: z.boolean().optional(),
    presentationType: PresentationTypeSchema.optional(),
    warningDays: WarningDaysSchema.optional(),
  })
  .strict();
export type VehicleDocumentTypeAdminUpdate = z.infer<typeof VehicleDocumentTypeAdminUpdateSchema>;

export const VehicleDocumentTypeAdminResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  name: z.string(),
  requiresExpiration: z.boolean(),
  presentationType: PresentationTypeSchema,
  warningDays: z.number().int(),
  active: z.boolean(),
  /** false for a type shared by another company of the group (read-only here). */
  editable: z.boolean(),
  createdAt: z.string().datetime().or(z.date()),
});
export type VehicleDocumentTypeAdminResponse = z.infer<typeof VehicleDocumentTypeAdminResponseSchema>;

export const VehicleDocumentTypeAdminListResponseSchema = z.object({
  items: z.array(VehicleDocumentTypeAdminResponseSchema),
});
export type VehicleDocumentTypeAdminListResponse = z.infer<typeof VehicleDocumentTypeAdminListResponseSchema>;
